import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input } from 'antd';
import ExcelTable from 'components/ui/excel-table';
import {
  ECellValueType,
  EExcelTablePaginationType,
  ExcelTableColDef,
  ECellValueDisplayMode,
  RowData,
} from 'components/ui/excel-table/types';
import { TOTAL_ROW_KEY } from 'components/ui/excel-table/constants';
import { CatPricingAnalysisRow, CatPricingAnalysisTableProps } from './types';
import { validateCatPricingAnalysisRow } from './forms';
import FilterIcon from 'assets/icons/filter';
import ExportV2Icon from 'assets/icons/export-v2';
import SearchIcon from 'assets/icons/search';
import { CountrySelection } from 'components/ui/country-selection';
import { TFunction } from 'i18next';
import {
  BasisOfCoverResponseDtoBasisOfCoverEnum,
  GetTailoredResultQueryDtoPriceRatingTypeEnum,
  GetTailoredResultQueryDtoTailoredResultTypeEnum,
} from 'libs/pinnacle-sdk';
import 'styles/ant-design.css';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import { layerApi, priceRatingApi, scheduleOfValuesApi } from 'services/apis';
import usePagination from 'hooks/usePagination';
import { ESocketEvents } from 'hooks/useWebSocket';
import { isSubmissionEditable } from 'utils/submission';
import { isNil } from 'lodash';

const CatPricingAnalysisTable = forwardRef<
  {
    refetch: () => Promise<unknown>;
  },
  CatPricingAnalysisTableProps
>(
  (
    {
      onErrorChange,
      basisOfCover,
      submissionId,
      submissionStatus,
      peril,
      sendMessage,
      currency,
      originalCurrency,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const {
      currentPage,
      pageSize,
      onPageChange,
      resetPagination,
      onShowSizeChange,
    } = usePagination();
    const [form] = Form.useForm();
    const country = Form.useWatch('country', form);
    const searchText = Form.useWatch('searchText', form);

    const isEditingMode = useMemo(() => {
      return isSubmissionEditable(submissionStatus);
    }, [submissionStatus]);

    useEffect(() => {
      resetPagination();
    }, [country, resetPagination, searchText]);

    const shouldEnableEditing = useMemo(() => {
      if (!originalCurrency) return isEditingMode;

      return currency === originalCurrency && isEditingMode;
    }, [currency, originalCurrency, isEditingMode]);

    const {
      data: catPricingAnalysis,
      isLoading: isCatPricingAnalysisLoading,
      refetch: refetchCatPricingAnalysis,
    } = useQuery(
      [
        EQueryKeys.CAT_PRICING_ANALYSIS,
        submissionId,
        peril,
        currentPage,
        pageSize,
        searchText,
        currency,
        country,
      ],
      () =>
        priceRatingApi.getCatPricingAnalysis(submissionId, peril, {
          includeTotalRow: true,
          page: currentPage,
          pageSize,
          searchText,
          currency,
          countries: isNil(country) ? undefined : [country],
        }),
      {
        refetchOnWindowFocus: false,
      }
    );

    useImperativeHandle(ref, () => ({
      refetch: () => refetchCatPricingAnalysis(),
    }));

    const { data: layersData } = useQuery(
      [EQueryKeys.FILTER_LAYERS, submissionId],
      () =>
        layerApi.filterLayers({
          filterLayersQueryDto: { submissionId },
        }),
      {
        refetchOnWindowFocus: false,
      }
    );

    // Generate column definitions based on the basis of cover
    const columnDefs = useMemo<ExcelTableColDef[]>(() => {
      const cols: ExcelTableColDef[] = [];

      // Property column (only if basis of cover is ANY_ONE_LOCATION)
      if (
        basisOfCover === BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneLocation
      ) {
        cols.push({
          field: 'property',
          headerName: t('catPricingAnalysis.headers.property', 'Property'),
          flex: 1,
          minWidth: 150,
          editable: shouldEnableEditing,
          resizable: true,
        });
      }

      cols.push({
        field: 'crestaZone',
        headerName: t('catPricingAnalysis.headers.crestaZone', 'Cresta Zone'),
        flex: 1,
        minWidth: 150,
        editable: shouldEnableEditing,
        resizable: true,
      });

      if (
        basisOfCover === BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneEvent ||
        basisOfCover === BasisOfCoverResponseDtoBasisOfCoverEnum.InTheAggregate
      ) {
        cols.push({
          field: 'numberOfLocations',
          headerName: t(
            'catPricingAnalysis.headers.numberOfLocations',
            'Number of Locations'
          ),
          flex: 1,
          minWidth: 150,
          type: ECellValueType.NUMERIC_COLUMN,
          editable: shouldEnableEditing,
          resizable: true,
          precision: 0,
        });
      }

      cols.push(
        {
          field: 'catMultiplier',
          headerName: t(
            'catPricingAnalysis.headers.catMultiplier',
            'Cat Multiplier'
          ),
          flex: 1,
          minWidth: 150,
          type: ECellValueType.NUMERIC_COLUMN,
          editable: shouldEnableEditing,
          resizable: true,
        },
        {
          field: 'tiv',
          headerName: t('catPricingAnalysis.headers.tiv', 'TIV'),
          flex: 1,
          minWidth: 150,
          type: ECellValueType.NUMERIC_COLUMN,
          editable: shouldEnableEditing,
          resizable: true,
          precision: 0,
        },
        {
          field: 'catRate',
          headerName: t('catPricingAnalysis.headers.catRate', 'Cat Rate'),
          flex: 1,
          minWidth: 150,
          type: ECellValueType.NUMERIC_COLUMN,
          displayMode: ECellValueDisplayMode.PERCENTAGE,
          editable: shouldEnableEditing,
          precision: 3,
          resizable: true,
        },
        {
          field: 'catRiskPremium',
          headerName: t(
            'catPricingAnalysis.headers.catRiskPremium',
            'CAT Risk Premium (100%)'
          ),
          flex: 1,
          minWidth: 180,
          type: ECellValueType.NUMERIC_COLUMN,
          editable: shouldEnableEditing,
          resizable: true,
          precision: 0,
        }
      );

      // Add columns for each layer's expected loss cost
      (layersData?.items || []).forEach(({ layerNumber }) => {
        cols.push({
          field: `layer${layerNumber}ExpectedLossCost`,
          headerName: t('catPricingAnalysis.headers.layerExpectedLossCost', {
            layer: `Layer ${layerNumber}`,
          }),
          flex: 1,
          minWidth: 180,
          type: ECellValueType.NUMERIC_COLUMN,
          editable: shouldEnableEditing,
          resizable: true,
          precision: 0,
        });
      });

      return cols;
    }, [basisOfCover, t, shouldEnableEditing, layersData?.items]);

    const rowData = useMemo<CatPricingAnalysisRow[]>(() => {
      const generateRowData = (): CatPricingAnalysisRow[] => {
        return (catPricingAnalysis?.items || []).map((item) => {
          const layerSummaries = item.layerConnections.map(
            (layerConnection) => ({
              expectedLostCost: layerConnection.expectedLostCost,
              layerNumber: layerConnection.layer.layerNumber,
            })
          );
          const layerTransformedData = layerSummaries.reduce(
            (acc, curr) => {
              acc[`layer${curr.layerNumber}ExpectedLossCost`] =
                curr.expectedLostCost;
              return acc;
            },
            {} as Record<string, string>
          );
          return {
            crestaZone: item.crestaZone,
            property: item.property || '',
            numberOfLocations: item.numberOfLocations || '',
            catMultiplier: item.catMultiplier,
            tiv: item.totalInsurableValue,
            catRate: (Number(item.catRate) * 100).toString(),
            catRiskPremium: item.catRiskPremium,
            id: item.id,
            ...layerTransformedData,
          };
        });
      };
      return generateRowData();
    }, [catPricingAnalysis]);

    const validateRow = useCallback(
      (
        row: CatPricingAnalysisRow,
        rowIndex: number,
        _: CatPricingAnalysisRow[],
        t: TFunction
      ) => {
        return validateCatPricingAnalysisRow(row, rowIndex, t);
      },
      []
    );

    const summaryRowData = useMemo(() => {
      const totalRowTitleColumn =
        basisOfCover === BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneLocation
          ? 'property'
          : 'crestaZone';
      const totalLocations =
        catPricingAnalysis?.totalRow?.numberOfLocations || '';
      const totalTIV = catPricingAnalysis?.totalRow?.totalInsurableValue || '';
      const totalCatRiskPremium =
        catPricingAnalysis?.totalRow?.catRiskPremium || '';

      const layerTotals = catPricingAnalysis?.layersElcTotal?.reduce(
        (acc, layer) => {
          acc[`layer${layer.layerNumber}ExpectedLossCost`] =
            layer.totalExpectedLossCost;
          return acc;
        },
        {} as Record<string, string>
      );

      return {
        id: TOTAL_ROW_KEY,
        [totalRowTitleColumn]: '',
        crestaZone:
          basisOfCover ===
          BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneLocation
            ? ''
            : 'Grand Total',
        property:
          basisOfCover ===
          BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneLocation
            ? 'Grand Total'
            : '',
        numberOfLocations: totalLocations,
        catMultiplier: undefined, // Default value to satisfy type requirements
        tiv: totalTIV,
        catRate: undefined, // Default value to satisfy type requirements
        catRiskPremium: totalCatRiskPremium,
        ...layerTotals,
      };
    }, [
      basisOfCover,
      catPricingAnalysis?.totalRow?.numberOfLocations,
      catPricingAnalysis?.totalRow?.totalInsurableValue,
      catPricingAnalysis?.totalRow?.catRiskPremium,
      catPricingAnalysis?.layersElcTotal,
    ]);

    const { data: countryListData } = useQuery(
      [EQueryKeys.SOV_COUNTRIES, submissionId],
      () =>
        scheduleOfValuesApi.getSovCountryList({
          submissionId: submissionId,
        }),
      {
        refetchOnWindowFocus: false,
      }
    );

    const onDataChange = useCallback(
      async (affectedRow: RowData<CatPricingAnalysisRow>) => {
        if (affectedRow.isError.strict) return;
        sendMessage(ESocketEvents.ON_DATA_CHANGE, {
          submissionId,
          tailoredResultType:
            GetTailoredResultQueryDtoTailoredResultTypeEnum.CatPricingAnalysis,
          priceRatingType:
            GetTailoredResultQueryDtoPriceRatingTypeEnum.CatRating,
          data: {
            catMultiplier: affectedRow.data.catMultiplier
              ? Number(affectedRow.data.catMultiplier)
              : undefined,
            catRate: affectedRow.data.catRate
              ? Number(affectedRow.data.catRate) / 100
              : undefined,
            catPricingAnalysisId: affectedRow.data.id,
          },
        });
      },
      [submissionId, sendMessage]
    );
    return (
      <div className="rounded-lg bg-white">
        <Form form={form} className="w-full">
          <div className="flex items-center justify-between mb-5">
            <div className="w-56 custom-form-item">
              <CountrySelection
                name="country"
                label=""
                includeCountryFlag={false}
                countryList={countryListData}
              />
            </div>

            <div className="flex gap-2">
              <Form.Item
                name="searchText"
                className="m-0"
                style={{ width: '320px' }}
              >
                <Input
                  prefix={<SearchIcon />}
                  placeholder={t('general.search', 'Search')}
                  size="large"
                  className="w-[320px] rounded-xl shadow-md"
                />
              </Form.Item>
              <button className="p-2 rounded-xl shadow-md border hover:bg-gray-50">
                <FilterIcon />
              </button>
              <button className="flex items-center gap-2 px-6 py-2 shadow-md bg-[#1d67b6] text-white rounded-lg hover:bg-[#1d67b6]/90 transition-colors">
                <ExportV2Icon />
                {t('catPricingAnalysis.export', 'Export')}
              </button>
            </div>
          </div>
        </Form>

        <ExcelTable
          rowKeyColName="id"
          baseTableHeight={490}
          tableHeightBias={370}
          className="mb-4"
          columnDefs={columnDefs}
          data={rowData}
          tableClassName="rounded-t-lg"
          onErrorChange={({ strict }) => {
            onErrorChange?.(strict);
          }}
          paginationType={EExcelTablePaginationType.DETAIL}
          includeIdRow={false}
          includeAddRow={false}
          includeDeleteCol={false}
          validateRow={validateRow}
          loading={isCatPricingAnalysisLoading}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onShowSizeChange}
          totalRows={catPricingAnalysis?.pagination.total || 0}
          summaryRowData={summaryRowData}
          onDataChange={isEditingMode ? onDataChange : undefined}
        />
      </div>
    );
  }
);

CatPricingAnalysisTable.displayName = 'CatPricingAnalysisTable';
export default CatPricingAnalysisTable;
